import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import { HiddenInput, Input, Select } from 'components/Shared';
import { FORM_BACK_ACTION } from 'constants/Common';
import { useValidation } from 'hooks';
import React, { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from 'store';
import {
  FetchJustificationActions as FetchActions,
  UpdateJustificationActions as UpdateActions,
} from 'store/ducks/justifications';
import { UpdateJustificationValidator } from 'validators/Justifications';
import * as S from './styles';
import { FetchJustificationTypesActions } from 'store/ducks/justificationTypes';

interface Props {
  justificationId: string | number;
  onUpdate?: () => void;
}

export const JustificationUpdateForm: React.FC<Props> = ({ justificationId, onUpdate }) => {
  const dispatch: AppDispatch = useDispatch();
  const formRef = useRef<FormHandles>(null);
  const { handleFormErrors, handleApiErrors } = useValidation();

  const { data: justification } = useSelector((state: RootState) => state.fetchJustification);

  const { loading: updatingJustification, validationErrors } = useSelector(
    (state: RootState) => state.updateJustification
  );

  const fetchJustification = useCallback((): void => {
    dispatch(FetchActions.request(justificationId));
  }, [dispatch, justificationId]);

  const { loading: justificationTypesLoading, data: justificationTypes  } = useSelector(
    (state: RootState) => state.fetchJustificationTypes
  );

  const fetchJustificationTypes = useCallback((): void => {
    dispatch(FetchJustificationTypesActions.request());
  }, []);

  const justificationTypesOptions =  justificationTypes.map((type) => ({ label: type.name, value: type.id }))

  const onJustificationLoad = useCallback((): void => {
    if (!justification) return;

    const { id, description, typeJustification } = justification;

    if(id){
      formRef.current?.setFieldValue('justificationId', id);
    }
    if(description){
      formRef.current?.setFieldValue('description', description);
    }

    if(typeJustification && typeJustification.length > 0) {
      formRef.current?.setFieldValue('type', [{
        label: typeJustification[0].name,
        value: typeJustification[0].id
      }]);
    }

  }, [justification]);

  const onSuccess = useCallback((): void => {
    formRef?.current?.reset();
    onUpdate && onUpdate();
  }, [onUpdate]);

  const onSubmit = useCallback(
    async (data: any): Promise<void> => {
      try {
        formRef?.current?.setErrors({});

        const { schema } = new UpdateJustificationValidator();

        const validData = await schema.validate(data, {
          abortEarly: false,
        });

        dispatch(UpdateActions.request(justificationId, validData, onSuccess));
      } catch (error) {
        handleFormErrors(error, formRef);
      }
    },
    [justificationId, dispatch, handleFormErrors, onSuccess]
  );

  useEffect(() => {
    fetchJustification();
    fetchJustificationTypes()
  }, [fetchJustification, fetchJustificationTypes]);

  useEffect(() => {
    onJustificationLoad();
  }, [onJustificationLoad]);

  useEffect(() => {
    handleApiErrors(validationErrors, formRef);
  }, [handleApiErrors, validationErrors]);

  useEffect(() => {
    return () => {
      dispatch(FetchActions.reset());
      dispatch(UpdateActions.reset());
    };
  }, [dispatch]);

  return (
    <S.Container>
      <Form ref={formRef} onSubmit={onSubmit}>
        <HiddenInput name="justificationId" />
          <S.FormRowUpdate>
            <Input name="description" label="Descrição" />
            <Select placeholder="Selecione um tipo" name="type" label="tipo" options={justificationTypesOptions} isLoading={justificationTypesLoading} />
          </S.FormRowUpdate>
        <S.FormActions>
          <S.LinkButton mood="light" to="/configuracoes/justificativas">
            {FORM_BACK_ACTION}
          </S.LinkButton>
          <S.Button type="submit">
            {updatingJustification ? <S.ActivityIndicator /> : 'Salvar'}
          </S.Button>
        </S.FormActions>
      </Form>
    </S.Container>
  );
};
